/*
 * Booking widget
 *   - Wires flatpickr (range mode) across the Arrive + Depart inputs
 *   - Clicking either input opens the same picker
 *   - On submit, normalises date values to Y-m-d for the results URL
 *
 * Flatpickr is loaded globally via wp_enqueue_script (CDN).
 */

const DISPLAY_FORMAT = 'M j, Y';   // "Apr 22, 2026"
const SUBMIT_FORMAT  = 'Y-m-d';

// Hard floor on bookings — Mews properties don't accept reservations before
// this date. Used as flatpickr's minDate when it's still in the future;
// otherwise we fall back to "today".
const EARLIEST_BOOKING = '2026-07-20';

function earliestBookingDate() {
    const launch = new Date(EARLIEST_BOOKING + 'T00:00:00');
    const today  = new Date();
    today.setHours(0, 0, 0, 0);
    return launch.getTime() > today.getTime() ? launch : today;
}

function initWidget(form) {
    if (form.dataset.wired === '1') return;
    form.dataset.wired = '1';

    const arriveEl = form.querySelector('.js-booking-arrive');
    const departEl = form.querySelector('.js-booking-depart');
    if (!arriveEl || !departEl || typeof flatpickr !== 'function') return;

    // Two-letter weekday labels in the calendar header (Su Mo Tu We Th Fr Sa).
    if (flatpickr.l10ns && flatpickr.l10ns.default) {
        flatpickr.l10ns.default.weekdays.shorthand = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    }

    // Hidden mirror inputs store the Y-m-d values actually submitted.
    const arriveHidden = document.createElement('input');
    arriveHidden.type  = 'hidden';
    arriveHidden.name  = 'arrive';
    const departHidden = document.createElement('input');
    departHidden.type  = 'hidden';
    departHidden.name  = 'depart';

    form.appendChild(arriveHidden);
    form.appendChild(departHidden);

    // Dedicated anchor for flatpickr to bind to. Keeps flatpickr's
    // date-string writes (which in range mode produce "Y-m-d to Y-m-d"
    // concatenations) entirely off our user-facing display inputs, so
    // we can drive arriveEl + departEl independently without fighting
    // the picker over who owns their values.
    //
    // Flatpickr forces `type="text"` on its bound input (it needs to be
    // focusable + readable) and also calls .focus() on it when opening
    // the picker. We pin the anchor to a fixed on-screen position (viewport
    // top-left, 0×0) so the focus call can't trigger the browser's
    // scroll-into-view — which on mobile would otherwise jump the page
    // down to wherever the form sits in the document flow.
    const anchor = document.createElement('input');
    anchor.type     = 'text';
    anchor.tabIndex = -1;
    anchor.setAttribute('aria-hidden', 'true');
    anchor.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;opacity:0;pointer-events:none;border:0;padding:0;margin:0;';
    form.appendChild(anchor);

    // Stop the visible inputs from being submitted with their own names.
    arriveEl.removeAttribute('name');
    departEl.removeAttribute('name');
    arriveEl.removeAttribute('required');
    departEl.removeAttribute('required');

    // Seed hidden values if the PHP partial pre-filled the display inputs.
    if (arriveEl.value) arriveHidden.value = isoFromAny(arriveEl.value);
    if (departEl.value) departHidden.value = isoFromAny(departEl.value);

    const minDate = earliestBookingDate();

    // When the widget lives inside the availability bar, after flatpickr
    // positions naturally (anchored horizontally to the date input), nudge
    // the calendar's TOP down to sit flush with the bar's bottom edge.
    const bar = form.closest('.availability__bar');

    const alignToBarBottom = (instance) => {
        if (!bar) return;
        const rect = bar.getBoundingClientRect();
        const cal  = instance.calendarContainer;
        // flatpickr uses absolute positioning with body as the offset parent,
        // so add the document scroll to the viewport-relative bottom.
        cal.style.top = `${rect.bottom + window.scrollY}px`;
    };

    // 2-night minimum stay. We can't simply disable arrive+1 in flatpickr's
    // range mode — disabled cells act as range boundaries (the range can't
    // span them), so it'd block valid departs too. Instead, after a full
    // range is picked we check the gap; if it's < 2 nights, snap depart
    // forward to arrive + 2 days so the minimum is always met.
    const MIN_NIGHTS = 2;
    const DAY_MS = 86400000;
    const enforceMinNights = (instance, selectedDates) => {
        if (selectedDates.length !== 2) return false;
        const [s, e] = selectedDates;
        const nights = Math.round((e - s) / DAY_MS);
        if (nights >= MIN_NIGHTS) return false;

        const snapped = new Date(s);
        snapped.setDate(snapped.getDate() + MIN_NIGHTS);
        instance.setDate([s, snapped], true);
        return true; // tells caller the range was rewritten — bail to avoid double-handling
    };

    const picker = flatpickr(anchor, {
        mode: 'range',
        minDate,
        dateFormat: SUBMIT_FORMAT,
        altInput: false,
        showMonths: 1,
        monthSelectorType: 'static',
        // Anchor's hidden — we open manually from the visible inputs.
        clickOpens: false,
        // Position the calendar relative to the arrive input so it pops
        // under the visible field, not the offscreen anchor.
        positionElement: arriveEl,
        onReady: (_d, _s, instance) => {
            // Seed flatpickr with any pre-filled range from PHP/URL.
            if (arriveHidden.value && departHidden.value) {
                instance.setDate([arriveHidden.value, departHidden.value], false);
                writeDisplay(arriveEl, arriveHidden.value);
                writeDisplay(departEl, departHidden.value);
            } else if (arriveHidden.value) {
                instance.setDate([arriveHidden.value], false);
                writeDisplay(arriveEl, arriveHidden.value);
            }
            // Replace the default chevron arrows with horizontal-line arrows.
            replaceMonthArrows(instance);
            // Allow click-and-drag across day cells to pick a range.
            enableDragSelect(instance);
            // Toggle the prev-month arrow's visibility based on whether we're
            // already at minDate (can't go further back).
            updatePrevArrow(instance, minDate);
        },
        onOpen: (_d, _s, instance) => {
            alignToBarBottom(instance);
            // Re-seed the picker from our saved values when reopening,
            // so a previously-picked arrive date is highlighted again
            // in the calendar. Necessary because flatpickr's range mode
            // auto-clears its internal selection when the picker closes
            // with an incomplete range.
            if (!instance.selectedDates.length && arriveHidden.value) {
                const start = new Date(arriveHidden.value + 'T00:00:00');
                const end   = departHidden.value ? new Date(departHidden.value + 'T00:00:00') : null;
                instance.setDate(end && !isNaN(end.getTime()) ? [start, end] : [start], false);
            }
        },
        // Blur the visible inputs when the picker closes so mobile's
        // :focus { font-size: 16px } iOS-zoom guard releases the inputs
        // back to their resting size.
        onClose: () => {
            arriveEl.blur();
            departEl.blur();
        },
        onMonthChange: (_d, _s, instance) => updatePrevArrow(instance, minDate),
        onYearChange:  (_d, _s, instance) => updatePrevArrow(instance, minDate),
        onChange: (selectedDates, _dateStr, instance) => {
            // No auto-clear here — flatpickr fires onChange with [] when
            // the picker closes on an incomplete range, and we don't want
            // that to wipe out a valid arrive date the user already picked.
            // Explicit clears come through the form's Reset handler.
            if (!selectedDates.length) return;

            // If a full range was just picked but the gap is < 2 nights,
            // snap depart forward. setDate(..., true) triggers onChange
            // again with the corrected range, so bail here and let the
            // recursive call do the rest.
            if (enforceMinNights(instance, selectedDates)) return;

            arriveHidden.value = formatISO(selectedDates[0]);
            writeDisplay(arriveEl, arriveHidden.value);

            if (selectedDates[1]) {
                departHidden.value = formatISO(selectedDates[1]);
                writeDisplay(departEl, departHidden.value);
            } else {
                departHidden.value = '';
                departEl.value = '';
            }
        }
    });

    // Clicking either visible input (or its row icons) opens the picker.
    const openPicker = (e) => {
        if (e) e.preventDefault();
        picker.open();
    };

    // Block the inputs from receiving focus on click. Without this the
    // browser auto-scrolls the freshly-focused input into view (causing
    // the page to jump when the calendar appears) and on mobile would
    // also apply any :focus styles to it. mousedown.preventDefault stops
    // focus without cancelling the subsequent click event, so the
    // picker still opens.
    const blockFocus = (e) => e.preventDefault();
    arriveEl.addEventListener('mousedown', blockFocus);
    departEl.addEventListener('mousedown', blockFocus);

    arriveEl.addEventListener('click', openPicker);
    departEl.addEventListener('click', openPicker);

    form.querySelectorAll('.booking-widget__date-icon').forEach((icon) => {
        icon.style.cursor = 'pointer';
        icon.addEventListener('click', openPicker);
    });

    // Clear button resets everything.
    form.addEventListener('reset', () => {
        picker.clear();
        arriveHidden.value = '';
        departHidden.value = '';
        setTimeout(() => {
            arriveEl.value = '';
            departEl.value = '';
        }, 0);
    });

    // Submit guard — require a full range.
    form.addEventListener('submit', (e) => {
        if (!arriveHidden.value || !departHidden.value) {
            e.preventDefault();
            picker.open();
            form.classList.add('is-invalid');
            return;
        }
        form.classList.remove('is-invalid');
    });
}

function enableDragSelect(instance) {
    let dragStart = null;
    let downPos   = null;

    const isSelectable = (el) =>
        el &&
        el.classList.contains('flatpickr-day') &&
        !el.classList.contains('flatpickr-disabled') &&
        !el.classList.contains('prevMonthDay') &&
        !el.classList.contains('nextMonthDay');

    instance.calendarContainer.addEventListener('mousedown', (e) => {
        const day = e.target.closest('.flatpickr-day');
        if (!isSelectable(day)) return;
        // Stop the browser from starting a text-selection range across the
        // cells (which paints as a blue rectangle in the gap between them).
        e.preventDefault();
        dragStart = day;
        downPos   = { x: e.clientX, y: e.clientY };
    });

    instance.calendarContainer.addEventListener('mouseup', (e) => {
        const dayUp    = e.target.closest('.flatpickr-day');
        const startDay = dragStart;
        const startPos = downPos;
        dragStart = null;
        downPos   = null;
        if (!startDay || !startPos) return;
        if (!isSelectable(dayUp) || dayUp === startDay) return;
        // Only treat it as a drag if the mouse actually moved.
        const dx = e.clientX - startPos.x;
        const dy = e.clientY - startPos.y;
        if (Math.sqrt(dx * dx + dy * dy) < 4) return;
        if (!startDay.dateObj || !dayUp.dateObj) return;
        const a = startDay.dateObj;
        const b = dayUp.dateObj;
        const range = a.getTime() <= b.getTime() ? [a, b] : [b, a];
        instance.setDate(range, true);
    });

    instance.calendarContainer.addEventListener('mouseleave', () => {
        dragStart = null;
        downPos   = null;
    });
}

// Hide the prev-month arrow when the displayed month is already at (or
// before) the earliest allowed month. Once the user navigates forward past
// minDate's month, it reappears.
function updatePrevArrow(instance, minDate) {
    const prev = instance.calendarContainer.querySelector('.flatpickr-prev-month');
    if (!prev) return;
    const atFloor =
        instance.currentYear < minDate.getFullYear() ||
        (instance.currentYear === minDate.getFullYear() &&
         instance.currentMonth <= minDate.getMonth());
    prev.classList.toggle('is-hidden', atFloor);
}

function replaceMonthArrows(instance) {
    const prev = instance.calendarContainer.querySelector('.flatpickr-prev-month');
    const next = instance.calendarContainer.querySelector('.flatpickr-next-month');
    const leftArrow =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">' +
        '<path d="M19 12H5M11 5l-7 7 7 7"/></svg>';
    const rightArrow =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">' +
        '<path d="M5 12h14M13 5l7 7-7 7"/></svg>';
    if (prev) prev.innerHTML = leftArrow;
    if (next) next.innerHTML = rightArrow;
}

function formatISO(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function isoFromAny(value) {
    // Accept Y-m-d already or any Date-parseable string; return Y-m-d.
    const d = new Date(value);
    return isNaN(d.getTime()) ? '' : formatISO(d);
}

function writeDisplay(el, iso) {
    const d = new Date(iso + 'T00:00:00');
    if (isNaN(d.getTime())) return;
    const opts = { month: 'short', day: 'numeric', year: 'numeric' };
    el.value = d.toLocaleDateString(undefined, opts);
}

export function initBookingWidgets() {
    document.querySelectorAll('.js-booking-widget').forEach(initWidget);
}
